import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { LayoutGrid, Flame } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FeedError } from "@/components/feed-error";
import { FeedCard, ExploreCard } from "@/components/feed-card";
import { DetailModal } from "@/components/detail-modal";
import { SearchHeader } from "@/components/feed/search-header";
import { FilterBar } from "@/components/feed/filter-bar";
import { FeedSkeletons, ExploreSkeleton } from "@/components/feed/skeletons";
import { EmptyState } from "@/components/feed/empty-state";
import { DEFAULT_FEED_FILTERS, QUERY_KEYS } from "@/lib/constants";
import { fetchSocialPosts } from "@/lib/api";
import { Platform, MediaType, PostStatus } from "@/types";
import type { FeedFilters, SocialPost } from "@/types";
import { useState, useCallback, useMemo, useEffect } from "react";
import { cn } from "@/lib/utils";

type TabMode = "feed" | "explore";

/* ─── Stories Bar (placeholder) ─── */
function StoriesBar() {
  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b overflow-x-auto scrollbar-hide">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex flex-col items-center gap-1.5 shrink-0">
          <div className="w-[60px] h-[60px] rounded-full bg-gradient-to-tr from-amber-400 via-fuchsia-500 to-purple-500 p-[2px]">
            <div className="w-full h-full rounded-full bg-background p-[2px]">
              <div className="w-full h-full rounded-full bg-muted" />
            </div>
          </div>
          <span className="text-[10px] text-muted-foreground">
            {["All", "IG", "TT", "FB", "YT", "TW"][i]}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── Tab Bar ─── */
function TabBar({ mode, onChange }: { mode: TabMode; onChange: (m: TabMode) => void }) {
  const tabs = [
    { id: "feed" as const, label: "Feed", icon: LayoutGrid },
    { id: "explore" as const, label: "Explore", icon: Flame },
  ];
  return (
    <div className="flex items-center justify-center border-b">
      <div className="flex">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => onChange(tab.id)}
              className={cn(
                "flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 transition-colors",
                mode === tab.id
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Feed Page ─── */
function FeedPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tabMode, setTabMode] = useState<TabMode>("feed");
  const [searchQuery, setSearchQuery] = useState("");
  const [detailPost, setDetailPost] = useState<SocialPost | null>(null);

  // Close modal on unmount (prevents portal from lingering across pages)
  useEffect(() => {
    return () => setDetailPost(null);
  }, []);

  const searchParams = useSearch({
    from: "/feed/",
    select: (search: Record<string, unknown>) => ({
      status: ((search["status"] as string) ?? DEFAULT_FEED_FILTERS.status) as PostStatus,
      platform: (search["platform"] as Platform | undefined) ?? undefined,
      mediaType: (search["mediaType"] as MediaType | undefined) ?? undefined,
    }),
  });

  const filters: FeedFilters = {
    status: searchParams.status,
    platform: searchParams.platform,
    mediaType: searchParams.mediaType,
    limit: DEFAULT_FEED_FILTERS.limit,
    offset: DEFAULT_FEED_FILTERS.offset,
  };

  const {
    data,
    isLoading,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey: QUERY_KEYS.socialPosts(filters),
    queryFn: () => fetchSocialPosts(filters),
    refetchInterval: 30000,
  });

  const posts = data?.transformedData ?? [];

  const setFilters = (newFilters: FeedFilters) => {
    navigate({
      to: "/feed",
      search: Object.fromEntries(
        Object.entries(newFilters).filter(([, v]) => v !== undefined),
      ) as Record<string, string>,
      replace: true,
    });
  };

  const refetch = () => {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.socialPosts(filters),
    });
  };

  const filteredPosts = useMemo(() => {
    let result = posts;

    if (filters.mediaType) {
      result = result.filter((post) => {
        if (filters.mediaType === MediaType.Carousel) {
          return post.media.length > 1;
        }
        return post.media.some((m) => m.type === filters.mediaType);
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (post) =>
          post.caption.toLowerCase().includes(q) ||
          post.socialAccounts[0]?.username?.toLowerCase().includes(q),
      );
    }

    return result;
  }, [posts, filters.mediaType, searchQuery]);

  const detailIndex = detailPost ? filteredPosts.findIndex((p) => p.id === detailPost.id) : -1;

  const openDetail = useCallback((post: SocialPost) => setDetailPost(post), []);
  const closeDetail = useCallback(() => setDetailPost(null), []);
  const goNextDetail = useCallback(() => {
    if (detailIndex >= 0 && detailIndex < filteredPosts.length - 1) {
      const next = filteredPosts[detailIndex + 1];
      if (next) setDetailPost(next);
    }
  }, [detailIndex, filteredPosts]);
  const goPrevDetail = useCallback(() => {
    if (detailIndex > 0) {
      const prev = filteredPosts[detailIndex - 1];
      if (prev) setDetailPost(prev);
    }
  }, [detailIndex, filteredPosts]);

  return (
    <div className="bg-background">
      {/* Layer 2: Feed-specific page shell */}
      <div className="sticky top-0 sm:top-14 z-40 bg-background/90 backdrop-blur-md border-b">
        <SearchHeader
          value={searchQuery}
          onChange={setSearchQuery}
          onRefresh={refetch}
          isRefreshing={isFetching}
        />
      </div>

      <TabBar mode={tabMode} onChange={setTabMode} />

      {tabMode === "feed" && <FilterBar filters={filters} onChange={setFilters} />}

      {/* Content Area */}
      <main className="pb-12">
        {isLoading ? (
          tabMode === "feed" ? (
            <FeedSkeletons />
          ) : (
            <ExploreSkeleton />
          )
        ) : queryError ? (
          <div className="px-4 py-12">
            <FeedError error={queryError as Error} onRetry={refetch} />
          </div>
        ) : filteredPosts.length === 0 ? (
          <EmptyState />
        ) : tabMode === "feed" ? (
          <div>
            <StoriesBar />
            <div className="divide-y divide-border/50">
              {filteredPosts.map((post) => (
                <FeedCard key={post.id} post={post} />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-0.5">
            {filteredPosts.map((post) => (
              <ExploreCard key={post.id} post={post} onClick={() => openDetail(post)} />
            ))}
          </div>
        )}
      </main>

      <DetailModal
        post={detailPost}
        onClose={closeDetail}
        onNext={detailIndex < filteredPosts.length - 1 ? goNextDetail : undefined}
        onPrev={detailIndex > 0 ? goPrevDetail : undefined}
      />
    </div>
  );
}

const feedSearchSchema = z.object({
  status: z.enum(["draft", "scheduled", "published", "failed"]).optional(),
  platform: z
    .enum(["facebook", "instagram", "linkedin", "tiktok", "youtube", "pinterest", "x"])
    .optional(),
  mediaType: z.enum(["image", "video", "carousel"]).optional(),
  post: z.string().optional(),
});

export const Route = createFileRoute("/feed/")({
  component: FeedPage,
  validateSearch: feedSearchSchema,
});
